import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ReportResult, TemplateMeta } from '../../lib/api';
import { useAsync } from '../../hooks/useApi';
import { FilterPanel } from './FilterPanel';
import { DataTable, isCurrencyColumn, isAgeColumn } from '../Charts/DataTable';
import { BarChart } from '../Charts/BarChart';
import { LineChart } from '../Charts/LineChart';
import { StackedBarChart } from '../Charts/StackedBarChart';
import { StatCards, StatCard } from '../Charts/StatCards';
import { Button } from '../Common/Button';
import { LoadingSpinner } from '../Common/LoadingSpinner';
import { FreshnessBanner } from '../Layout/FreshnessBanner';
import { exportToExcel, exportToPdf } from '../../lib/export';
import { showToast } from '../../hooks/useToast';
import { Modal } from '../Common/Modal';

function defaultValues(filters: TemplateMeta['filters']): Record<string, any> {
  const out: Record<string, any> = {};
  for (const f of filters) {
    if (f.type === 'dateRange') {
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - 90);
      out.dateFrom = from.toISOString().slice(0, 10);
      out.dateTo = to.toISOString().slice(0, 10);
    } else if (f.defaultValue !== undefined) {
      out[f.key] = f.defaultValue;
    }
  }
  return out;
}

function computeStatCards(template: TemplateMeta, rows: Record<string, any>[]): StatCard[] {
  if (template.key !== 'patient_balances' || !rows.length) return [];

  const sum = (col: string) => rows.reduce((s, r) => s + Number(r[col] ?? 0), 0);
  const avg = (col: string) => rows.length ? sum(col) / rows.length : 0;

  return [
    { label: 'Total Patient Balance', value: sum('Patient Balance'),  format: 'currency' },
    { label: 'Total Charges',         value: sum('Charge Amount'),    format: 'currency' },
    { label: 'Accounts with Balance', value: rows.length,             format: 'number' },
    { label: 'Avg Days Outstanding',  value: avg('Age (Days)').toFixed(0), format: 'number' },
  ];
}

export function TemplateRunner() {
  const { key } = useParams<{ key: string }>();
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [template, setTemplate] = useState<TemplateMeta | null>(null);
  const runResult = useAsync(api.runTemplate);
  const [saveModal, setSaveModal] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [chartTypeOverride, setChartTypeOverride] = useState<string | null>(null);

  useEffect(() => {
    api.listTemplates().then(r => {
      const t = r.templates.find(x => x.key === key) ?? null;
      setTemplate(t);
      if (t) setFilters(defaultValues(t.filters));
    });
  }, [key]);

  useEffect(() => {
    if (template && Object.keys(filters).length > 0) {
      // Auto-run with default filters
      runResult.run(template.key, filters);
    }
  }, [template]);

  const result: ReportResult | null = (runResult.data ?? null) as ReportResult | null;
  const cards = useMemo(
    () => template && result ? computeStatCards(template, result.rows) : [],
    [template, result],
  );
  const effectiveChart = chartTypeOverride ?? template?.chartType ?? 'table';

  if (!template) return <LoadingSpinner label="Loading template…" />;

  const stickyCount = template.key === 'patient_balances' ? 3 : 0;

  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-12 lg:col-span-3 space-y-4">
        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="text-sm font-semibold text-text-primary mb-1">{template.name}</div>
          <div className="text-xs text-text-muted mb-4 leading-relaxed">{template.description}</div>
          <FilterPanel filters={template.filters} values={filters} onChange={setFilters} />
          <Button
            variant="primary"
            className="mt-4 w-full"
            onClick={() => runResult.run(template.key, filters)}
            disabled={runResult.loading}
          >
            {runResult.loading ? 'Running…' : 'Run report'}
          </Button>
        </div>
      </div>

      <div className="col-span-12 lg:col-span-9 space-y-3">
        {result?.meta.isStale && (
          <FreshnessBanner ageHours={result.meta.ageHours} stagedAt={result.meta.stagedAt} />
        )}

        {runResult.loading && <LoadingSpinner label="Running query…" />}
        {runResult.error && <div className="text-danger text-sm">{runResult.error}</div>}

        {result && (
          <>
            {cards.length > 0 && <StatCards cards={cards} />}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                {(['bar', 'line', 'stacked', 'table'] as const).map(ct => (
                  <button
                    key={ct}
                    onClick={() => setChartTypeOverride(ct)}
                    className={`px-2.5 py-1 text-xs rounded-md transition-colors ${effectiveChart === ct ? 'bg-accent-muted text-accent-text' : 'text-text-muted hover:bg-page'}`}
                  >
                    {ct}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => setSaveModal(true)}>Save</Button>
                <Button size="sm" onClick={() => exportToExcel({
                  reportName: template.name,
                  params: filters,
                  columns: result.columns,
                  rows: result.rows,
                  runAt: new Date().toISOString(),
                  summaryCards: cards.map(c => ({
                    label: c.label,
                    value: typeof c.value === 'number'
                      ? (c.format === 'currency'
                          ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(c.value)
                          : String(c.value))
                      : String(c.value),
                  })),
                })}>Export Excel</Button>
                <Button size="sm" onClick={() => exportToPdf({
                  reportName: template.name,
                  params: filters,
                  columns: result.columns,
                  rows: result.rows,
                  runAt: new Date().toISOString(),
                  summaryCards: cards.map(c => ({
                    label: c.label,
                    value: typeof c.value === 'number'
                      ? (c.format === 'currency'
                          ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(c.value)
                          : String(c.value))
                      : String(c.value),
                  })),
                })}>Export PDF</Button>
              </div>
            </div>

            {effectiveChart !== 'table' && result.rows.length > 0 && (() => {
              const xKey = result.columns[0];
              const numericCols = result.columns.filter(c => isCurrencyColumn(c) || (!isAgeColumn(c) && typeof result.rows[0][c] === 'number'));
              const yKey = numericCols[0] ?? result.columns[1];
              if (!yKey) return null;
              return (
                <div className="bg-surface border border-border rounded-lg p-4">
                  {effectiveChart === 'bar' && <BarChart data={result.rows} xKey={xKey} yKey={yKey} />}
                  {effectiveChart === 'line' && <LineChart data={result.rows} xKey={xKey} yKey={yKey} />}
                  {effectiveChart === 'stacked' && <StackedBarChart data={result.rows} xKey={xKey} stackKeys={numericCols} />}
                </div>
              );
            })()}

            <DataTable
              columns={result.columns}
              rows={result.rows}
              defaultSort={template.key === 'patient_balances' ? { column: 'Patient Balance', dir: 'desc' } : undefined}
              stickyColumns={stickyCount}
            />
          </>
        )}
      </div>

      <Modal
        open={saveModal}
        onClose={() => setSaveModal(false)}
        title="Save report"
        footer={<>
          <Button onClick={() => setSaveModal(false)}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!saveName.trim()}
            onClick={async () => {
              try {
                await api.saveReport({
                  name: saveName,
                  reportType: 'template',
                  templateKey: template.key,
                  params: filters,
                  chartType: effectiveChart,
                });
                showToast('success', `Saved "${saveName}"`);
                setSaveModal(false);
                setSaveName('');
              } catch (err: any) {
                showToast('error', err.message ?? 'Save failed');
              }
            }}
          >
            Save
          </Button>
        </>}
      >
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-text-hint mb-1">Report name</label>
          <input
            type="text"
            value={saveName}
            onChange={e => setSaveName(e.target.value)}
            placeholder={template.name}
            className="w-full px-2.5 py-1.5 text-sm bg-surface border border-border rounded-md focus:outline-none focus:border-accent"
          />
        </div>
      </Modal>
    </div>
  );
}
